import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import { Scrollbar } from '@/components/Scrollbar';
import {
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import AsaphDialog from '@/components/shared/Dialog/AsaphDialog';
import { PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import { ChatType, GetChatQuery } from '@/graphql';
import { getChatTitleMembersRecipient } from '@/helpers/getChatChannelInfo';
import { useAuth } from '@/providers/AuthProvider';
import { Fade, Slide } from '@material-ui/core';
import { pathOr } from 'ramda';

import styles from './PrivateGroupSettingsPanel.module.scss';
import { PrivateGroupSettingsRoot } from './PrivateGroupSettingsRoot';
import { SettingEditGroup } from './SettingEditGroup';
import { SettingGroupMembers } from './SettingGroupMembers';

export enum EPrivateGroupSettingsDisplayState {
	ROOT,
	EDIT_GROUP,
	GROUP_MEMBERS,
}

interface IProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	chat: GetChatQuery['getChat'];
	loadingChatChannel: boolean;
	loadChatChannel: () => Promise<any>;
}

export interface IPropsPrivateGroupSettingsDisplay {
	pushDisplayState: (displayState: EPrivateGroupSettingsDisplayState) => void;
	popDisplayState: () => void;
	setOpen: (open: boolean) => void;
	panelTransitionTimeout: number;
	chat: IProps['chat'];
	loadingChatChannel: boolean;
	loadChatChannel: IProps['loadChatChannel'];
	title: string;
	membersNames: string[];
}

const displayStateComponents: Map<
	EPrivateGroupSettingsDisplayState,
	{
		title?: string;
		component: React.FC<IPropsPrivateGroupSettingsDisplay>;
	}
> = new Map([
	[
		EPrivateGroupSettingsDisplayState.ROOT,
		{ component: PrivateGroupSettingsRoot },
	],
	[
		EPrivateGroupSettingsDisplayState.EDIT_GROUP,
		{
			title: 'Edit group name',
			component: SettingEditGroup,
		},
	],
	[
		EPrivateGroupSettingsDisplayState.GROUP_MEMBERS,
		{
			title: 'Group members',
			component: SettingGroupMembers,
		},
	],
]);
const displayStateKeys = Array.from(displayStateComponents.keys());

export const PrivateGroupSettingsPanel: React.FC<IProps> = ({
	chat,
	...props
}) => {
	const { user } = useAuth();
	const [displayStateStack, setDisplayStateStack] = useState<
		EPrivateGroupSettingsDisplayState[]
	>([EPrivateGroupSettingsDisplayState.ROOT]);
	const [displayState, setDisplayState] =
		useState<EPrivateGroupSettingsDisplayState>(
			getDisplayState(displayStateStack),
		);

	const panelTitle = getNameForDisplayState(displayState);

	useEffect(() => {
		setDisplayState(getDisplayState(displayStateStack));
	}, [displayStateStack]);

	const { title, membersNames } = getChatTitleMembersRecipient(
		chat,
		user.id,
		user.displayName,
	);

	return (
		<AsaphDialog open={props.open} onClose={handleClose}>
			<div className={styles['private-group-settings-panel__toolbar']}>
				<AsaphDialogHeader
					leadingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: renderHeaderStart(),
					}}
					title={{
						needsFadeIn: !!panelTitle,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						label: panelTitle,
					}}
					trailingButton={{
						needsFadeIn: false,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: null,
					}}
				/>
			</div>
			{displayState !== EPrivateGroupSettingsDisplayState.ROOT && (
				<div
					className={
						styles['private-group-settings-panel__toolbar-divider']
					}
				>
					<hr />
				</div>
			)}
			<section
				className={styles['private-group-settings-panel__content']}
			>
				{displayStateKeys.map((key) => {
					const displayStateComponent =
						displayStateComponents.get(key);
					if (!displayStateComponent) {
						return null;
					}
					const Component = displayStateComponent.component;
					if (key === EPrivateGroupSettingsDisplayState.ROOT) {
						return (
							<Fade
								key={key}
								in={displayState === key}
								timeout={PANEL_TRANSITION_TIMEOUT}
								mountOnEnter
								unmountOnExit
							>
								<Scrollbar
									className={
										styles[
											'private-group-settings-panel__content-container'
										]
									}
								>
									<Component
										pushDisplayState={pushDisplayState}
										popDisplayState={popDisplayState}
										setOpen={props.setOpen}
										panelTransitionTimeout={
											PANEL_TRANSITION_TIMEOUT
										}
										chat={chat}
										loadingChatChannel={
											props.loadingChatChannel
										}
										loadChatChannel={props.loadChatChannel}
										title={title}
										membersNames={membersNames}
									/>
								</Scrollbar>
							</Fade>
						);
					}

					return (
						<Slide
							key={key}
							direction="left"
							in={
								displayState === key ||
								displayStateStack.indexOf(key) >= 0
							}
							timeout={PANEL_TRANSITION_TIMEOUT}
							mountOnEnter
							unmountOnExit
						>
							<Scrollbar
								className={
									styles[
										'private-group-settings-panel__content-container'
									]
								}
								// Needed to prevent underlying Components' absolute positioned elements be on top of new Component
								style={{
									zIndex: displayStateStack.length,
								}}
							>
								<Component
									pushDisplayState={pushDisplayState}
									popDisplayState={popDisplayState}
									setOpen={props.setOpen}
									panelTransitionTimeout={
										PANEL_TRANSITION_TIMEOUT
									}
									chat={chat}
									loadingChatChannel={
										props.loadingChatChannel
									}
									loadChatChannel={props.loadChatChannel}
									title={title}
									membersNames={membersNames}
								/>
							</Scrollbar>
						</Slide>
					);
				})}
			</section>
		</AsaphDialog>
	);

	function pushDisplayState(newState: EPrivateGroupSettingsDisplayState) {
		setDisplayStateStack([...displayStateStack, newState]);
	}

	function popDisplayState() {
		if (displayStateStack.length > 1) {
			const newStack = [...displayStateStack];
			newStack.pop();
			setDisplayStateStack(newStack);
		}
	}

	function handleClose() {
		props.setOpen(false);
		popDisplayState();
	}

	function renderHeaderStart(): ReactNode {
		switch (displayState) {
			case EPrivateGroupSettingsDisplayState.EDIT_GROUP:
			case EPrivateGroupSettingsDisplayState.GROUP_MEMBERS:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Back"
						handlePress={popDisplayState}
					/>
				);
			default:
				return (
					<AsaphDialogHeaderButton
						type="text"
						label="Done"
						handlePress={handleClose}
					/>
				);
		}
	}

	function renderHeaderEnd(): ReactNode {
		return null;
	}
};

function getDisplayState(
	displayStateStack: EPrivateGroupSettingsDisplayState[],
) {
	return displayStateStack.length
		? displayStateStack[displayStateStack.length - 1]
		: EPrivateGroupSettingsDisplayState.ROOT;
}

function getNameForDisplayState(
	displayState: EPrivateGroupSettingsDisplayState,
): string {
	const displayStateComponent = displayStateComponents.get(displayState);
	if (displayStateComponent) {
		return pathOr('', ['title'], displayStateComponent);
	}
	return '';
}
